import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Logo } from '@vd/ui';
import {
  DEFAULT_MCP_SCOPES,
  MCP_SCOPE_LABELS,
  OPT_IN_MCP_SCOPES,
  expandRequestedScopes,
  type McpScope,
} from '@vd/types';
import { useAuth } from '../features/auth/AuthProvider';
import { get, post } from '../lib/apiClient';

/**
 * Generic OAuth 2.1 consent screen for dynamically-registered MCP clients
 * (Claude Code, Claude Desktop, any future remote MCP client).
 *
 * Different from `/oauth/extension`: that one has hardcoded copy for the
 * Chrome extension. This page handles the generic DCR flow with a full
 * scope picker. Server validates everything again — this page just saves
 * a round-trip on obviously-broken params.
 */

// Shared with the MCP token modal, so both describe a scope the same way.
const SCOPE_META = MCP_SCOPE_LABELS;

const PRESETS: { key: string; label: string; scopes: McpScope[] }[] = [
  { key: 'read',    label: 'Read-only',      scopes: ['mcp.read'] },
  { key: 'comment', label: 'Read + comment', scopes: ['mcp.read', 'mcp.comments'] },
  // Everything but the opt-in scopes (Jira send), which a client asks for by name and a person ticks.
  { key: 'full',    label: 'Full access',    scopes: [...DEFAULT_MCP_SCOPES] },
];

/**
 * What the consent screen starts with ticked: the most permissive preset
 * still entirely within what the client asked for, which the person can
 * narrow before approving. An opt-in scope (sending to Jira) is never ticked
 * for them, even when no preset fits: the person ticks it (Codex review,
 * batch 4).
 */
export function initialGrant(requestedScopes: McpScope[]): Set<McpScope> {
  const requested = new Set(requestedScopes);
  const matchingPreset = [...PRESETS].reverse().find((p) => p.scopes.every((s) => requested.has(s)));
  return new Set(matchingPreset?.scopes
    ?? requestedScopes.filter((s) => !(OPT_IN_MCP_SCOPES as readonly string[]).includes(s)));
}

const CHALLENGE_RE = /^[A-Za-z0-9_-]{43,128}$/;
const STATE_RE     = /^[A-Za-z0-9_-]{8,200}$/;

interface ParsedParams {
  clientId: string;
  state: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  redirectUri: string;
  requestedScopes: McpScope[];
}

interface ClientInfo {
  id: string;
  name: string;
  kind: string;
  redirectUris: string[];
  meta?: Record<string, unknown>;
}

function parseParams(params: URLSearchParams): { ok: true; value: ParsedParams } | { ok: false; reason: string } {
  const clientId            = params.get('client_id') ?? '';
  const state               = params.get('state') ?? '';
  const codeChallenge       = params.get('code_challenge') ?? '';
  const codeChallengeMethod = params.get('code_challenge_method') ?? '';
  const redirectUri         = params.get('redirect_uri') ?? '';
  const scopeRaw            = params.get('scope') ?? 'mcp';

  if (!clientId) return { ok: false, reason: 'Missing client_id.' };
  if (!STATE_RE.test(state)) return { ok: false, reason: 'Missing or malformed state parameter.' };
  if (!CHALLENGE_RE.test(codeChallenge)) return { ok: false, reason: 'Missing or malformed code_challenge.' };
  if (codeChallengeMethod !== 'S256') return { ok: false, reason: 'Only S256 PKCE is supported.' };
  if (!redirectUri) return { ok: false, reason: 'Missing redirect_uri.' };

  const requestedScopes = expandRequestedScopes(scopeRaw.split(/\s+/).filter(Boolean));
  if (requestedScopes.length === 0) {
    return { ok: false, reason: 'No valid scopes requested.' };
  }

  return { ok: true, value: { clientId, state, codeChallenge, codeChallengeMethod, redirectUri, requestedScopes } };
}

async function fetchClientInfo(clientId: string): Promise<ClientInfo | null> {
  try {
    return await get<ClientInfo>(`/v1/oauth/clients/${encodeURIComponent(clientId)}`);
  } catch {
    return null;
  }
}

async function authorize(parsed: ParsedParams, grantedScopes: McpScope[]): Promise<string> {
  // Through the API client, which names the account this page was built for:
  // consent shown to one account must not be granted to another that has
  // since signed in from another tab (review of 0.2.0, 3 Oct).
  const { code } = await post<{ code: string }>('/v1/oauth/authorize', {
    clientId: parsed.clientId,
    redirectUri: parsed.redirectUri,
    codeChallenge: parsed.codeChallenge,
    codeChallengeMethod: parsed.codeChallengeMethod,
    scopes: grantedScopes,
  });
  return code;
}

function redirectBack(redirectUri: string, params: Record<string, string>) {
  const url = new URL(redirectUri);
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  window.location.replace(url.toString());
}

// ---- Sub-components ------------------------------------------------------

function ConsentHeader({ client, userEmail, redirectUri }: {
  client: ClientInfo;
  userEmail: string;
  redirectUri: string;
}) {
  return (
    <>
      <div style={{ fontSize: 14, marginBottom: 8 }}>
        <strong>{client.name}</strong> wants access to your Toado account as
        <strong style={{ marginLeft: 4 }}>{userEmail}</strong>.
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Redirect URL: <code style={{ fontSize: 11 }}>{redirectUri}</code>
      </div>
    </>
  );
}

function ScopePresets({ requestedScopes, activeKey, onApply }: {
  requestedScopes: McpScope[];
  activeKey: string | null;
  onApply: (scopes: McpScope[]) => void;
}) {
  const requested = new Set(requestedScopes);
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
      {PRESETS.map((p) => {
        const disabled = !p.scopes.every((s) => requested.has(s));
        const active = activeKey === p.key;
        return (
          <button
            key={p.key}
            type="button"
            onClick={() => onApply(p.scopes)}
            disabled={disabled}
            style={{
              flex: 1,
              padding: '8px 12px',
              fontSize: 13,
              border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
              background: active ? 'var(--bg-subtle)' : 'var(--surface)',
              color: disabled ? 'var(--text-tertiary)' : 'var(--text)',
              borderRadius: 8,
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontWeight: active ? 500 : 400,
            }}
            title={disabled ? `${p.label} includes scopes the app didn't request.` : p.label}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

function ScopeCustomize({ requestedScopes, granted, onToggle }: {
  requestedScopes: McpScope[];
  granted: Set<McpScope>;
  onToggle: (s: McpScope) => void;
}) {
  return (
    <details style={{ marginBottom: 12 }}>
      <summary style={{ fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
        Customize scopes ({granted.size} of {requestedScopes.length} requested)
      </summary>
      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {requestedScopes.map((s) => {
          const meta = SCOPE_META[s];
          const checked = granted.has(s);
          return (
            <label
              key={s}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: 10,
                border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 8,
                cursor: 'pointer',
                background: checked ? 'var(--bg-subtle)' : 'var(--surface)',
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(s)}
                style={{ marginTop: 2 }}
                data-testid={`scope-${s}`}
              />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{meta.title}</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{meta.desc}</div>
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2, fontFamily: 'JetBrains Mono, monospace' }}>
                  {s}
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </details>
  );
}

function ConsentActions({ busy, canApprove, onApprove, onDeny }: {
  busy: boolean;
  canApprove: boolean;
  onApprove: () => void;
  onDeny: () => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
      <Button variant="ghost" onClick={onDeny} disabled={busy}>Cancel</Button>
      <Button variant="primary" onClick={onApprove} disabled={busy || !canApprove} style={{ flex: 1 }}>
        {busy ? 'Authorizing…' : 'Approve'}
      </Button>
    </div>
  );
}

function ErrorCard({ title, message }: { title: string; message: string }) {
  return (
    <div style={{ maxWidth: 480, margin: '48px auto', padding: 24, color: 'var(--text)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Logo size={28} />
        <h1 style={{ fontSize: 16, margin: 0, color: 'var(--danger)' }}>{title}</h1>
      </div>
      <div style={{ padding: 16, borderRadius: 8, background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', fontSize: 13, color: 'var(--text)' }}>
        {message}
      </div>
    </div>
  );
}

// ---- Page ---------------------------------------------------------------

export function OAuthAuthorizePage() {
  const [searchParams] = useSearchParams();
  const parseResult = useMemo(() => parseParams(searchParams), [searchParams]);
  const { user } = useAuth();

  const [client, setClient] = useState<ClientInfo | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  const [grantedScopes, setGrantedScopes] = useState<Set<McpScope>>(() => {
    return parseResult.ok ? initialGrant(parseResult.value.requestedScopes) : new Set();
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!parseResult.ok) return;
    let cancelled = false;
    fetchClientInfo(parseResult.value.clientId)
      .then((info) => {
        if (cancelled) return;
        if (!info) {
          setClientError('This application is not registered. It may have been revoked or the client_id is wrong.');
          return;
        }
        if (!info.redirectUris.includes(parseResult.value.redirectUri)) {
          setClientError('The redirect URL this application is using is not registered. This often means a phishing attempt. Stop and verify.');
          return;
        }
        setClient(info);
      })
      .catch((e) => { if (!cancelled) setClientError((e as Error).message); });
    return () => { cancelled = true; };
  }, [parseResult]);

  // Never inside a frame (security double check, 2026-10-11): the app is a
  // static site that cannot send `frame-ancestors`, and an Approve button
  // another page can frame is what clickjacking wants. Checked before the
  // sign-in redirect, which would otherwise navigate the frame. The widget's
  // sign-in page refuses the same way.
  if (window.top !== window.self) {
    return <ErrorCard title="Open this in its own tab" message="Toado only asks you to approve access in a page of its own, never inside another site." />;
  }
  if (!parseResult.ok) {
    return <ErrorCard title="Bad authorization request" message={parseResult.reason} />;
  }
  const parsed = parseResult.value;

  if (!user) {
    const returnUrl = window.location.href;
    window.location.replace(`/signin?next=${encodeURIComponent(returnUrl)}`);
    return <div style={{ padding: 24 }}>Redirecting to sign in…</div>;
  }
  if (clientError) {
    return <ErrorCard title="Application not authorized" message={clientError} />;
  }
  if (!client) {
    return <div style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading…</div>;
  }

  const onApprove = async () => {
    setBusy(true);
    setError(null);
    try {
      const granted = [...grantedScopes];
      if (granted.length === 0) {
        setError('Select at least one scope before approving.');
        setBusy(false);
        return;
      }
      const code = await authorize(parsed, granted);
      redirectBack(parsed.redirectUri, { code, state: parsed.state });
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const onDeny = () => {
    redirectBack(parsed.redirectUri, {
      error: 'access_denied',
      error_description: 'User declined the authorization request.',
      state: parsed.state,
    });
  };

  const toggleScope = (s: McpScope) => {
    setGrantedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  };
  const applyPreset = (scopes: McpScope[]) => setGrantedScopes(new Set(scopes));

  const activePreset = PRESETS.find(
    (p) => p.scopes.length === grantedScopes.size && p.scopes.every((s) => grantedScopes.has(s)),
  );

  return (
    <div style={{ maxWidth: 520, margin: '48px auto', padding: 24, color: 'var(--text)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Logo size={32} />
        <h1 style={{ fontSize: 18, margin: 0, fontWeight: 600 }}>Authorize application</h1>
      </div>

      <div style={{ padding: 20, borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <ConsentHeader client={client} userEmail={user.email} redirectUri={parsed.redirectUri} />

        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Grant access level</div>
        <ScopePresets
          requestedScopes={parsed.requestedScopes}
          activeKey={activePreset?.key ?? null}
          onApply={applyPreset}
        />
        <ScopeCustomize
          requestedScopes={parsed.requestedScopes}
          granted={grantedScopes}
          onToggle={toggleScope}
        />

        {error && (
          <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 12 }}>{error}</div>
        )}

        <ConsentActions
          busy={busy}
          canApprove={grantedScopes.size > 0}
          onApprove={onApprove}
          onDeny={onDeny}
        />
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 16, textAlign: 'center' }}>
        You can revoke this access any time at <code>/me/settings/tokens</code>.
      </div>
    </div>
  );
}
